import { Button } from '@lms/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@lms/ui/card';
import { Checkbox } from '@lms/ui/checkbox';
import { ColorInput } from '@lms/ui/color-input';
import { Input } from '@lms/ui/input';
import { Label } from '@lms/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@lms/ui/select';
import { useToast } from '@lms/ui/use-toast';
import { useCallback, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useSWRConfig } from 'swr';
import { useTranslations } from 'use-intl';
import {
  PageBox,
  PageButtons,
  PageHeader,
  PageTitle,
} from '@/components/admin/page-box';
import { DropZone } from '@/components/dropzone';
import type { Site } from '@/features/branding/brand-provider';
import { type Changes, saveSettings } from './settings-api';

type Props = {
  site: Site;
};

// Ports features/branding/components/brand-settings.tsx (the Settings form).
// The Next version received the site as a prop and called a server action; here
// the site still arrives as a prop (from the BrandProvider) and the save runs
// the async `saveSettings` wrapper, then refreshes the live brand via SWR
// `mutate(['site', slug])` in place of the server action's `revalidatePath`.
export function BrandSettings({ site }: Props) {
  const [changes, setChanges] = useState<Changes | undefined>();
  const t = useTranslations();
  const { toast } = useToast();
  const { brand: slug } = useParams<{ brand: string }>();
  const { mutate } = useSWRConfig();

  const saveChanges = useCallback(
    async (changes: Changes) => {
      try {
        await saveSettings(changes, site.id);
        if (slug) await mutate(['site', slug]);
        toast({ title: t('admin.saved_changes') });
        setChanges(undefined);
      } catch (e) {
        toast({
          title: t('global.error'),
          description: `${e}`,
          variant: 'destructive',
        });
      }
    },
    [t, toast, site, slug, mutate],
  );

  return (
    <PageBox size="sm">
      <PageHeader>
        <PageTitle>{t('admin.settings')}</PageTitle>
        <PageButtons>
          <Button
            variant="outline"
            onClick={() => setChanges(undefined)}
            disabled={changes === undefined}
          >
            {t('admin.discard')}
          </Button>
          <Button
            disabled={changes === undefined}
            onClick={() => changes && saveChanges(changes)}
          >
            {t('admin.save')}
          </Button>
        </PageButtons>
      </PageHeader>

      <div>
        <Card className="mb-8 w-full">
          <CardHeader>
            <CardTitle>{t('admin.base_settings')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              <div className="grid gap-3">
                <Label htmlFor="name">{t('admin.name')}</Label>
                <Input
                  type="text"
                  className="w-full"
                  defaultValue={changes?.name || site.name}
                  onChange={(ev) =>
                    setChanges((c) => ({ ...c, name: ev.target.value }))
                  }
                />
              </div>
              <div className="grid gap-3">
                <Label htmlFor="description">{t('admin.slug')}</Label>
                <Input
                  type="text"
                  defaultValue={site.slug}
                  className="w-full"
                  disabled
                />
              </div>
              <div className="flex items-center space-x-2">
                <Checkbox
                  name="hasGroups"
                  checked={
                    !(changes?.hasGroups !== undefined
                      ? changes.hasGroups
                      : site.hasGroups)
                  }
                  onClick={() =>
                    setChanges((c) => ({
                      ...c,
                      hasGroups:
                        changes?.hasGroups !== undefined
                          ? !changes.hasGroups
                          : !site.hasGroups,
                    }))
                  }
                />
                <label
                  htmlFor="hasGroups"
                  className="text-sm leading-none font-medium peer-disabled:cursor-not-allowed peer-disabled:opacity-70"
                >
                  {t('admin.hide_groups')}
                </label>
              </div>
              <div className="grid gap-3">
                <Label htmlFor="groupAlias">{t('admin.group_alias')}</Label>
                <Select
                  value={changes?.groupAlias || site.groupAlias}
                  onValueChange={(v) =>
                    setChanges((c) => ({
                      ...c,
                      groupAlias: v as Site['groupAlias'],
                    }))
                  }
                >
                  <SelectTrigger>
                    <SelectValue
                      placeholder={t(`group_alias.${site.groupAlias}_singular`)}
                    />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="groups">
                      {t('group_alias.groups_singular')}
                    </SelectItem>
                    <SelectItem value="events">
                      {t('group_alias.events_singular')}
                    </SelectItem>
                    <SelectItem value="stores">
                      {t('group_alias.stores_singular')}
                    </SelectItem>
                    <SelectItem value="tenants">
                      {t('group_alias.tenants_singular')}
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="mb-8 w-full">
          <CardHeader>
            <CardTitle>{t('admin.images')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-6">
              <div className="grid gap-3">
                <Label htmlFor="name">{t('admin.logo')}</Label>
                <DropZone
                  onUploaded={(url) => setChanges((c) => ({ ...c, logo: url }))}
                  currentImage={changes?.logo || site.logo}
                  folder="logo"
                  tag={`logo-${site.id}`}
                />
              </div>
              <div className="grid gap-3">
                <Label htmlFor="description">{t('admin.poster')}</Label>
                <DropZone
                  currentImage={changes?.poster || site.poster}
                  onUploaded={(url) =>
                    setChanges((c) => ({ ...c, poster: url }))
                  }
                  folder="poster"
                  tag={`poster-${site.id}`}
                />
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="w-full">
          <CardHeader>
            <CardTitle>{t('admin.colors')}</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid gap-3">
              <Label htmlFor="name">{t('admin.primary')}</Label>
              <ColorInput
                value={changes?.primaryColor || site.primaryColor}
                onChange={(v) => {
                  setChanges((c) => ({
                    ...c,
                    primaryColor: v,
                  }));
                }}
              />
            </div>
          </CardContent>
        </Card>
      </div>
    </PageBox>
  );
}
